/* eslint-disable max-lines */
import { Container, Row, Col, Button, Form, FormGroup, Input, Card, Label, Table } from 'reactstrap';
import { useContext, useState, useRef, ChangeEvent, useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import HireAgreementsService from '../../../services/HireAgreements';
import EmployeeService from '../../../services/Employee';
import { toast } from 'react-toastify';
import CommonHeader from '../../../components/Headers/CommonHeader';
import CardHeaderTopFormButtons from '../../../components/Cards/CardHeaderTopFormButtons';
import { createOptionsPage } from '../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../entities/PagedMeta';

type AgreementCategory = 'HIRING' | 'MANAGEMENT';

const HireAgreementsNew = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id && location.pathname.endsWith('/edit');
  const isAssignMode = !!id && !isEditMode;

  const { token, currentSchool }: any = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(isAssignMode ? 1 : 0);

  const [agreementName, setAgreementName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentFileUrl, setCurrentFileUrl] = useState<string | null>(null);
  const [addSignaturePage, setAddSignaturePage] = useState(true);
  const [category, setCategory] = useState<AgreementCategory>('HIRING');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchStaffName, setSearchStaffName] = useState('');

  useEffect(() => {
    const fetchInitialData = async () => {
      if ((isAssignMode || isEditMode) && !agreementName) {
        try {
          const result = await HireAgreementsService(token).show(id);
          setAgreementName(result.data.name);
          setAddSignaturePage(!!result.data.addSignaturePage);
          setCategory((result.data.category as AgreementCategory) || 'HIRING');
          setCurrentFileUrl(result.data.file || null);
        } catch (e) {
          toast.error('Could not fetch agreement details.');
          history.push('/employee-settings');
        }
      }
      if (step === 1 && !isEditMode) {
        try {
          const filteredOptions = {
            filters: [
              {
                field: 'status',
                operation: 'in',
                value: ['HIRED', 'ACTIVE'],
              },
            ],
            options: createOptionsPage(createPagedMeta({ perPage: 1000 })),
          };

          const result = await EmployeeService(token).listEmployeesBySchool(currentSchool.value, '', filteredOptions);
          setEmployees(
            (result?.data || []).filter(
              (employee: any) => employee?.status === 'HIRED' || employee?.status === 'ACTIVE',
            ),
          );
        } catch (e: any) {
          toast.error(e?.response?.data?.message || 'Failed to load employees.');
        }
      }
    };
    fetchInitialData();
  }, [id, isAssignMode, isEditMode, step, token, currentSchool, history, agreementName]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 0) {
      if (!agreementName) {
        toast.error('Name is required');
        return;
      }
      if (!isEditMode && !selectedFile) {
        toast.error('File is required');
        return;
      }
      if (isEditMode) {
        return handleSaveAndContinue(e);
      }
      setStep(1);
    }
  };

  const handleBack = () => {
    if (step > 0 && !isAssignMode && !isEditMode) {
      setStep(step - 1);
    } else {
      history.push('/employee-settings');
    }
  };

  const handleSaveAndContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      if (isEditMode) {
        await HireAgreementsService(token).update(id, {
          name: agreementName,
          addSignaturePage,
          category,
        });
        if (selectedFile) {
          const formData = new FormData();
          formData.append('file', selectedFile);
          await HireAgreementsService(token).file(id, formData);
        }
        toast.success('Agreement updated successfully!');
      } else if (isAssignMode) {
        await HireAgreementsService(token).assignEmployees(id, { employeeIds: selectedEmployees });
        toast.success(`Agreement assignment updated for ${selectedEmployees.length} employee(s)!`);
      } else {
        const data = {
          name: agreementName,
          addSignaturePage,
          employeeIds: selectedEmployees,
          category,
        };
        const result = await HireAgreementsService(token).create(data);
        const agreementId = result?.data?.id;

        if (selectedFile && agreementId) {
          const formData = new FormData();
          formData.append('file', selectedFile);
          await HireAgreementsService(token).file(agreementId, formData);
        }
        toast.success('Agreement sent successfully!');
      }
      history.push('/employee-settings');
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'An error occurred!');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map((emp) => emp.id));
    }
    setSelectAll(!selectAll);
  };

  const toggleEmployeeSelection = (employeeId: string) => {
    const newSelection = selectedEmployees.includes(employeeId)
      ? selectedEmployees.filter((id) => id !== employeeId)
      : [...selectedEmployees, employeeId];
    setSelectedEmployees(newSelection);
    setSelectAll(newSelection.length === employees.length);
  };

  const pageTitle = isEditMode
    ? `Edit Agreement: "${agreementName || ''}"`
    : isAssignMode
    ? `Assign Agreement: "${agreementName || ''}"`
    : 'New Hire Agreement';

  return (
    <>
      <CommonHeader />
      <Container className="mt--8" fluid>
        <Row>
          <Col className="order-xl-2" xl="12">
            <Card className="bg-secondary shadow">
              <CardHeaderTopFormButtons pageTitle={pageTitle} buttons={[{ label: 'Back', onClick: handleBack }]} />

              {step === 0 && !isAssignMode && (
                <div className="pt-2 pl-5 pr-5 pb-4">
                  <Form onSubmit={handleContinue}>
                    <Row>
                      <Col lg="8">
                        <FormGroup>
                          <Label className="form-control-label" htmlFor="agreement-name">
                            Name
                          </Label>
                          <Input
                            id="agreement-name"
                            value={agreementName}
                            onChange={(e) => setAgreementName(e.target.value)}
                            placeholder="Example: Executive Policy"
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="4">
                        <FormGroup>
                          <Label className="form-control-label" htmlFor="agreement-category">
                            Category
                          </Label>
                          <Input
                            id="agreement-category"
                            type="select"
                            value={category}
                            onChange={(e) => setCategory(e.target.value as AgreementCategory)}
                          >
                            <option value="HIRING">Hiring Agreement</option>
                            <option value="MANAGEMENT">Management / Other</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <Label className="form-control-label">Attach File Here</Label>
                        <div>
                          <Button color="primary" onClick={() => fileInputRef.current?.click()}>
                            Select File
                          </Button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                            accept=".pdf"
                          />
                          {selectedFile && <span className="ml-3">Selected File: {selectedFile.name}</span>}
                          {!selectedFile && isEditMode && currentFileUrl && (
                            <span className="ml-3 text-muted">
                              Keep current file (
                              <a href={currentFileUrl} target="_blank" rel="noopener noreferrer">
                                view
                              </a>
                              )
                            </span>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row className="mt-4">
                      <Col>
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="checkbox"
                              checked={addSignaturePage}
                              onChange={() => setAddSignaturePage(!addSignaturePage)}
                            />{' '}
                            Add Signature Acknowledgement Page
                          </Label>
                          <div className="ml-4 mt-2 text-muted">
                            <p className="mb-0">Print Name</p>
                            <p className="mb-0">Signature</p>
                            <p className="mb-0">Date Of Signature</p>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row className="mt-5">
                      <Col className="text-right">
                        <Button color="success" type="submit" disabled={loading}>
                          {isEditMode ? (loading ? 'Saving...' : 'Save') : 'Continue'}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </div>
              )}

              {step === 1 && !isEditMode && (
                <div className="pt-4 pl-5 pr-5 pb-4">
                  <Form onSubmit={handleSaveAndContinue}>
                    <h2 className="text-center mb-4">Who Needs To Receive This Policy To Sign?</h2>
                    <Row className="mb-3">
                      <Col lg="4">
                        <Input
                          placeholder="Search By Name..."
                          value={searchStaffName}
                          onChange={(e) => setSearchStaffName(e.target.value)}
                        />
                      </Col>
                    </Row>
                    <Table className="align-items-center table-flush" responsive>
                      <thead className="thead-light">
                        <tr>
                          <th>
                            <Input type="checkbox" checked={selectAll} onChange={toggleSelectAll} />
                          </th>
                          <th>Name</th>
                          <th>Department</th>
                          <th>Employment Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employees?.map((employee) => {
                          const fullName = `${employee?.user?.firstName} ${employee.user?.lastName}`;
                          const isVisible =
                            searchStaffName === '' || fullName.toLowerCase().includes(searchStaffName.toLowerCase());

                          return (
                            <tr key={employee?.id} style={{ display: isVisible ? '' : 'none' }}>
                              <td>
                                <Input
                                  type="checkbox"
                                  checked={selectedEmployees.includes(employee?.id)}
                                  onChange={() => toggleEmployeeSelection(employee?.id)}
                                />
                              </td>
                              <td>{fullName}</td>
                              <td>{employee?.department?.name || '-'}</td>
                              <td>
                                {employee?.workday === 'FT' && 'Full Time'}
                                {employee?.workday === 'PT' && 'Part Time'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    <Row className="mt-5">
                      <Col className="text-right">
                        <Button color="success" type="submit">
                          Save & Continue
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default HireAgreementsNew;
